//NB: this will be empty in the browser
import { DEFAULT_URL_RULES, type UrlRules } from "~/config/url-rules";
import type { Locale } from "~/contexts";
import { buildProductUrl, slugifyProductName } from "~/hec/utils/product-slug";

import type { VariationGroup } from "../app.types";

/**
 * `https://host[:port]` of an absolute URL, or `null` for anything that does
 * not parse (relative paths, empty, garbage). Isomorphic — used by the CSP
 * builder (server) and the Constructor override resource hints (render).
 */
export function originOf(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

export const toProductUrl = (product?: { id?: string; name?: string }) => {
  if (!product?.id) return "#";
  return buildProductUrl(product.id, product.name);
};
export const toVariantURL = (
  variationGroup: VariationGroup,
  productName?: string,
) => {
  const base = buildProductUrl(variationGroup.productId, productName);
  const search = Object.entries(variationGroup.variationValues || {})
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  return search ? `${base}?${search}` : base;
};

/**
 * Serialize search params byte-identically to prod (SFRA), so PLP/search URLs
 * (address bar, SSR `href`s, canonical, shared/marketing links) match. Mirrors
 * SFRA's `pushParamsToURL` + `sortParamsAlphabetically`
 * (org_hec/.../URLMethods/UpdateURL.js):
 *  - `q` first, then remaining keys `localeCompare`-sorted (stable order
 *    regardless of the click order facets were applied in);
 *  - serialized via `URLSearchParams` (so space → `+`, matching prod);
 *  - then `%2C` → literal `,` — `URLSearchParams.toString()` percent-encodes the
 *    comma, but it's an RFC-3986 query sub-delim, legal unescaped and decoded
 *    identically by every reader. Only `%2C` is reversed; all other
 *    percent-encodings stay (prod decodes only the comma).
 * Returns the query string WITHOUT a leading `?` (empty string when no params).
 * Apply at EVERY URL-write boundary (facet adapter, curated pills, pagination
 * `href`s, search). (SFCC-11657)
 */
export function serializeSearchParams(params: URLSearchParams): string {
  const sp = new URLSearchParams(params);
  const q = sp.get("q");
  if (q !== null) sp.delete("q");
  const entries = Array.from(sp.entries()).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  if (q !== null) entries.unshift(["q", q]);
  return new URLSearchParams(entries).toString().replace(/%2C/g, ",");
}

export function getDomainUrl(request: Request) {
  const host =
    request.headers.get("X-Forwarded-Host") ?? request.headers.get("host");
  if (!host) {
    throw new Error("Could not determine domain URL.");
  }
  const protocol = host.includes("localhost") ? "http" : "https";
  return `${protocol}://${host}`;
}

/**
 * Returns the application's origin.
 *
 * NOTE: This utility can only be used server-side after your application has been
 * initialized using the `_createApp` method (This happens in your /app/ssr.js file).
 *
 * @function
 * @returns {string} Returns the ORIGIN under which we are serving the page.
 * @example
 * import {getAppOrigin} from 'pwa-kit-react-sdk/utils/url'
 *
 * const url = `${getAppOrigin()}/path`
 */
export const getAppOrigin = () => {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  // `APP_ORIGIN` is a BARE global assigned per app-rendered request by
  // `app.server.ts`. On a cold isolate that has not rendered a document yet
  // (e.g. the Layer-1 SFRA dispatch in worker.server.ts), it is not merely
  // empty — it is undeclared, and a bare read throws ReferenceError. Guard
  // with `typeof` so callers get a clear Error instead (COM-446). Server code
  // that has the request in hand should pass its own origin rather than rely
  // on this global (see `absoluteUrl(path, baseOrigin)`).
  if (typeof APP_ORIGIN === "undefined" || !APP_ORIGIN) {
    throw new Error(
      "APP_ORIGIN is not set on this isolate yet (no app-rendered request has assigned it); pass the request origin explicitly",
    );
  }

  return APP_ORIGIN;
};
export const getAppCallbackOrigin = () => {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  // Same bare-global hazard as `getAppOrigin` (COM-446).
  if (
    typeof APP_CALLBACK_ORIGIN === "undefined" ||
    !APP_CALLBACK_ORIGIN ||
    typeof APP_ORIGIN === "undefined" ||
    !APP_ORIGIN
  ) {
    throw new Error(
      "APP_ORIGIN / APP_CALLBACK_ORIGIN are not set on this isolate yet (no app-rendered request has assigned them)",
    );
  }

  return APP_ORIGIN;
};

/**
 * A function that takes a path and qualifies it with the current host and protocol.
 * This function works on the client and on the server.
 *
 * @example
 * absoluteUrl(/women/dresses?color=black)
 *
 * // => http(s)://www.site.com/women/dresses?color=black
 * @param path
 * @param baseOrigin Optional `<protocol>//<host>` to qualify against. Server
 *   callers that hold the request (e.g. the Layer-1 SFRA dispatch, which runs
 *   BEFORE any app render has assigned the `APP_ORIGIN` global) must pass
 *   `url.origin` here; without it the global is read and a cold isolate throws
 *   (COM-446).
 * @returns {string|*}
 */
export const absoluteUrl = (path: string, baseOrigin?: string) => {
  // Collapse a leading run of slashes to one. A path like `//` or `//foo` is
  // otherwise a protocol-relative URL (network-path reference): `new URL` reads
  // everything after `//` as the authority, so `//` throws `Invalid URL` (empty
  // host) and `//foo` silently resolves to the wrong origin (`https://foo/`).
  // These reach us from marketing-email links (`?iitt=…`) whose builder joins a
  // trailing-slash base with a leading-slash path. The crash happened in
  // AppContextMiddleware (resolveLocaleFromUrl → getParamsFromPath) before any
  // route rendered → hard 500 (SFCC-11887 / SFCC-11923). Only touches strings
  // starting with 2+ slashes; normal paths and absolute URLs are untouched.
  const normalized = path.replace(/^\/{2,}/, "/");
  return new URL(normalized, baseOrigin ?? getAppOrigin()).toString();
};

/**
 * Modifies a given url by adding/updating query parameters.
 *
 * @param {string} url - The base url of the output url set.
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string} A URL with additional params
 * @example
 * import {rebuildPathWithParams} from '/path/to/utils/url'
 *
 * rebuildPathWithParams(
 *     '/en-GB/product/25501032M',
 *     {color: 'JJ2SKXX', size: 'MD'}
 * )
 *
 * // Returns
 * // '/en-GB/product/25501032M?color=JJ2SKXX&size=9MD'
 */
export const rebuildPathWithParams = (
  url: string,
  extraParams: { [key: string]: string | number },
) => {
  const [pathname, search] = url.split("?");
  const params = new URLSearchParams(search);

  // Apply any extra params.
  Object.keys(extraParams).forEach(key => {
    const value = extraParams[key];

    // 0 is a valid value as for a param
    if (!value && value !== 0) {
      params.delete(key);
    } else {
      params.set(key, value.toString());
    }
  });

  // Clean up any trailing `=` for params without values.
  const paramStr = params.toString().replace(/=&/g, "&").replace(/=$/, "");

  // Generate the newly updated url.
  return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ""}`;
};

/**
 * Builds a list of modified Urls with the provided params key and values,
 * preserving any search params provided in the original url.Optionally
 * you can pass and object used to set static params values.
 * @param {string} url - The base url of the output url set.
 * @param {string} key - The search params for the associated values
 * @param {Array} values - The search param values
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string[]} A list of URLs
 * @example
 * import {buildUrlSet} from '/path/to/utils/url'
 *
 * buildUrlSet(
 *     '/womens/clothing',
 *     'sort',
 *     ['price-high-to-low', 'price-low-to-high'],
 *     {offset: 0}
 * )
 *
 * // Returns
 * // ['/womens/clothing?sort=price-high-to-low', '/womens/clothing?sort=price-low-to-high']
 */
export const buildUrlSet = (
  url = "",
  key = "",
  values = [],
  extraParams = {},
) =>
  values.map(value =>
    rebuildPathWithParams(url, { [key]: value, ...extraParams }),
  );

/**
 * Given a category and the current locale returns an href to the product list page.
 *
 * @param {Object} category
 * @returns {string}
 */
/**
 * Build a category URL. Prefers `path` (the full multi-segment slug computed
 * during navigation tree walk, e.g. `personal-care/acne-and-skincare`) and
 * falls back to `id` for callers that don't have the parent context.
 *
 * SFCC URL Rules emit multi-segment URLs from the catalog tree; SCAPI
 * `getUrlMapping` only resolves the full path. Single-segment fallbacks may
 * not resolve through the catch-all dispatcher.
 */
export const categoryUrlBuilder = (category: {
  id?: string | number;
  path?: string;
}) => encodeURI(`/${category.path ?? category.id}`);

// ---------------------------------------------------------------------------
// Category slug derivation (`c_alternativeUrl` → URL slug)
// ---------------------------------------------------------------------------

/**
 * Derives a category's URL slug from its `c_alternativeUrl` custom attribute.
 *
 * Some SFCC categories have a different URL slug than their catalog id —
 * production URLs are derived from `c_alternativeUrl` (first non-empty path
 * segment), not the catalog id. Example: `joint-and-muscle-pain-relief` has
 * `c_alternativeUrl = /pain-relief-and-recovery/all-pain-relief-and-recovery`,
 * so its URL slug is `pain-relief-and-recovery`. Children of slug-aliased
 * parents inherit this slug as their parent path segment.
 *
 * Returns `undefined` for absent / cross-site / non-relative values; callers
 * should fall back to `category.id` in that case.
 */
export function deriveSlug(category: {
  c_alternativeUrl?: unknown;
}): string | undefined {
  const alt = category.c_alternativeUrl;
  if (typeof alt !== "string" || !alt.startsWith("/")) return undefined;
  return alt.split("/").filter(Boolean)[0];
}

/**
 * Slugify a category display name into a single URL path segment, matching
 * SFCC's catalog SEO-URL generation.
 *
 * Applies the BM-declared `<character-replacements>` from `url-rules.ts`
 * (e.g. `&`→`and`, `+`→`plus`, apostrophes/parens/etc. removed), then delegates
 * the platform normalization SFCC layers on top — lowercase, diacritic fold,
 * repeated-separator collapse, trim — to `slugifyProductName`. Verified against
 * the production catalog: "Pain"→`pain`, "Joint & Muscle Pain"→`joint-and-muscle-pain`,
 * "Caring Mill™"→`caring-mill`, "Brand"→`brand`, "Avène"→`avene`.
 */
export function slugifyCategoryName(
  name: string | null | undefined,
  rules: UrlRules = DEFAULT_URL_RULES,
): string {
  if (!name) return "";
  let s = name;
  for (const [char, replacement] of rules.characterReplacements) {
    s = s.split(char).join(replacement);
  }
  return slugifyProductName(s);
}

/**
 * The single source of truth for a category's URL path segment, shared by URL
 * generation (nav links, breadcrumb/PLP slug map) and resolution
 * (`category-url-index.server.ts`). Precedence mirrors SFRA's `getCategoryUrl`
 * (`org_hec/cartridge/models/categories.js`):
 *
 *   1. `c_alternativeUrl` override (first path segment, via `deriveSlug`)
 *   2. slugified display name (the URL-Rules default — NOT the catalog id)
 *   3. catalog id as a last resort (missing / empty display name)
 */
export function categorySegment(category: {
  id?: string | number;
  name?: string | null;
  c_alternativeUrl?: unknown;
}): string {
  return (
    deriveSlug(category) ??
    (slugifyCategoryName(category.name) || String(category.id ?? ""))
  );
}

export type CategorySlugMap = Record<string, string>;

/**
 * Build a flat `id → slug` map from a category tree (typically the layout's
 * `categoryTree` from `getCategory("root", { levels: 2 })`). For each
 * category, the slug is derived from `c_alternativeUrl` when set, else the
 * raw `id` is used. Walks `categories[]` recursively.
 *
 * Built once per request in the layout loader and shared with all client
 * consumers via `useLayoutData()`. Avoids per-component or per-loader
 * re-derivation, and avoids extra SCAPI calls for ancestor attrs.
 */
export function buildCategorySlugMap(
  tree: ReadonlyArray<{
    id?: string;
    name?: string | null;
    c_alternativeUrl?: unknown;
    categories?: unknown[];
  }>,
): CategorySlugMap {
  const map: CategorySlugMap = {};
  function walk(cat: {
    id?: string;
    name?: string | null;
    c_alternativeUrl?: unknown;
    categories?: unknown[];
  }) {
    // `categorySegment` mirrors SFCC: c_alternativeUrl override, else the
    // slugified display name (NOT the raw id). The id is only a last resort.
    if (cat.id) map[cat.id] = categorySegment(cat);
    cat.categories?.forEach(child => walk(child as Parameters<typeof walk>[0]));
  }
  tree.forEach(cat => walk(cat));
  return map;
}

/**
 * Transform a slash-separated category path by replacing each id segment with
 * its slug from the map. Falls back to the original segment when the id isn't
 * in the map (e.g., for categories beyond the layout's `levels: 2` fetch).
 *
 * Use at render time on `parentCategoryTree`-derived paths and any other
 * id-based category path (breadcrumbs, PLP filter pills) to surface the same
 * URL slugs the meganav already uses.
 */
export function applyCategorySlugMap(
  path: string,
  map: CategorySlugMap,
): string {
  if (!path) return path;
  return path
    .split("/")
    .map(seg => (seg ? (map[seg] ?? seg) : seg))
    .join("/");
}

export type CategoryUrlIndexNode = {
  id?: string;
  name?: string | null;
  c_alternativeUrl?: unknown;
  categories?: CategoryUrlIndexNode[];
};

const normalizeIndexPath = (p: string): string =>
  p.replace(/^\/+/, "").replace(/\/+$/, "").toLowerCase();

const alternativeUrlPath = (alt: unknown): string | undefined => {
  if (typeof alt !== "string" || !alt) return undefined;
  if (alt.startsWith("/")) return alt;
  try {
    // Absolute cross-site value (e.g. after a host rewrite) — index its path.
    return new URL(alt).pathname;
  } catch {
    return undefined;
  }
};

/**
 * Build a `normalized-url-path → categoryId` index from a category tree — the
 * inverse of `buildCategorySlugMap` and the resolution counterpart to the nav's
 * URL generation. For each node it indexes BOTH the computed canonical path
 * (`parentPath/categorySegment`, the URL-Rules default) AND the node's full
 * `c_alternativeUrl` when set (the override the link actually uses), so a
 * friendly category URL whose slug ≠ catalog id resolves back to its id
 * (e.g. `/health-condition/pain`→`chronic-pain`,
 * `/pain-relief-and-recovery/all-pain-relief-and-recovery`→its category).
 *
 * Pure + synchronous so it can be unit-tested without SCAPI;
 * `category-url-index.server.ts` wraps it with the SCAPI fetch + KV cache.
 * Keys are normalized exactly as `lookupUrlMapping` normalizes an incoming slug
 * (leading/trailing slashes stripped, lowercased).
 *
 * Two passes, so a computed canonical path always wins over an `alternativeUrl`
 * alias: a parent category whose `alternativeUrl` points at its all-state child
 * (e.g. `personal-care` → `/personal-care/all-personal-care`) must NOT shadow
 * that child — that URL is the `all-personal-care` subcategory's own page, and
 * resolving it to the parent renders the wrong PLP (SFCC-11632). Pass 1 indexes
 * every node's computed path; pass 2 adds `alternativeUrl` aliases only for
 * paths still unclaimed.
 */
export function buildCategoryUrlIndex(
  tree: ReadonlyArray<CategoryUrlIndexNode>,
): Record<string, string> {
  const index: Record<string, string> = {};
  const add = (path: string | undefined, id?: string) => {
    if (!path || !id) return;
    const key = normalizeIndexPath(path);
    if (key && !(key in index)) index[key] = id;
  };
  // Deferred `[alternativeUrlPath, id]` pairs, applied after every computed path.
  const aliases: Array<[string, string]> = [];
  const walk = (cat: CategoryUrlIndexNode, parentPath: string) => {
    const segment = categorySegment(cat);
    const path = parentPath ? `${parentPath}/${segment}` : segment;
    add(path, cat.id);
    const alt = alternativeUrlPath(cat.c_alternativeUrl);
    if (alt && cat.id) aliases.push([alt, cat.id]);
    cat.categories?.forEach(child => walk(child, path));
  };
  tree.forEach(root => walk(root, ""));
  for (const [alt, id] of aliases) add(alt, id);
  return index;
}

export type CategoryAltUrlMap = Record<string, string>;

/**
 * Resolve a category's `c_shopAll` to a canonical URL path. Relative values
 * (`/x/all-x`) are used directly; a `$url('Search-Show','cgid','X')$` pipeline
 * expression resolves to category `X`'s computed path via `idToPath`. Content
 * expressions (`$url('Page-Show','cid',…)$`) and anything unrecognized return
 * `undefined`.
 */
const shopAllToPath = (
  shopAll: unknown,
  idToPath: Record<string, string>,
): string | undefined => {
  if (typeof shopAll !== "string" || !shopAll) return undefined;
  const relative = alternativeUrlPath(shopAll);
  if (relative) return relative;
  const cgid = shopAll.match(/'cgid'\s*,\s*'([^']+)'/)?.[1];
  return cgid ? idToPath[cgid] : undefined;
};

/**
 * Reduce an all-state landing to a clean storefront path the Workers router
 * owns. Stored `c_alternativeUrl`/`c_shopAll` values are frequently absolute
 * SFRA URLs that carry the site path prefix (`/s/{SITE}/health-condition/
 * shop-all`); `new URL().pathname` keeps that prefix, so strip it. SFRA
 * controller URLs (`…/Page-Show`, `…/on/demandware.store/…/Search-Show`) aren't
 * storefront routes — drop them (return undefined) so callers fall back to the
 * slug path. Computed cgid paths from `idToPath` are already clean, so this is
 * a no-op on them. Without this the nav "Shop All" link and the breadcrumb
 * all-state crumb emit `/s/{SITE}/…` instead of `/health-condition/shop-all`
 * (SFCC-11713).
 */
const normalizeLandingPath = (path: string | undefined): string | undefined => {
  if (!path) return undefined;
  if (/\/on\/demandware\.store\/|\/[A-Za-z]+-Show(?:$|[/?])/.test(path)) {
    return undefined;
  }
  const stripped = path.replace(/^\/s\/[^/]+(?=\/)/, "");
  return stripped || undefined;
};

/**
 * Build a flat `id → all-state landing URL` map (full from-root path) so the
 * breadcrumb routes an ancestor crumb to the page that represents the category,
 * never the bare parent (which is "unused ever"). The landing is the category's
 * relative `c_alternativeUrl` if set, else its `c_shopAll` — relative, or a
 * `Search-Show` cgid resolved to that category's computed path. SCAPI's
 * `parentCategoryTree` returns only id+name per ancestor, so — like
 * `categorySlugMap` — this map is the breadcrumb's only source of the override.
 *
 * Covers the 5 top-level "Shop By" parents (`alternativeUrl` → `/x/all-x`) plus
 * `health-condition`, whose all-state is a separate "Shop All" child reached via
 * a `shopAll` cgid (`/health-condition/shop-all`). Categories with neither — and
 * content-page (`Page-Show`) shopAll values — are omitted, so callers fall back
 * to the slug path, their true canonical (SFCC-11632).
 */
export function buildCategoryAltUrlMap(
  tree: ReadonlyArray<{
    id?: string;
    name?: string | null;
    c_alternativeUrl?: unknown;
    c_shopAll?: unknown;
    categories?: unknown[];
  }>,
): CategoryAltUrlMap {
  // Pass 1: each category's computed canonical path (same rule as
  // buildCategoryUrlIndex), so a `shopAll` cgid can resolve to its target's URL.
  const idToPath: Record<string, string> = {};
  function walkPaths(
    cat: {
      id?: string;
      name?: string | null;
      c_alternativeUrl?: unknown;
      categories?: unknown[];
    },
    parentPath: string,
  ) {
    const segment = categorySegment(cat);
    const path = parentPath ? `${parentPath}/${segment}` : segment;
    if (cat.id) idToPath[cat.id] = `/${path}`;
    cat.categories?.forEach(child =>
      walkPaths(child as Parameters<typeof walkPaths>[0], path),
    );
  }
  tree.forEach(cat => walkPaths(cat, ""));

  // Pass 2: each category's all-state landing — alternativeUrl, else shopAll.
  const map: CategoryAltUrlMap = {};
  function walk(cat: {
    id?: string;
    c_alternativeUrl?: unknown;
    c_shopAll?: unknown;
    categories?: unknown[];
  }) {
    if (cat.id) {
      const landing = normalizeLandingPath(
        alternativeUrlPath(cat.c_alternativeUrl) ??
          shopAllToPath(cat.c_shopAll, idToPath),
      );
      if (landing) map[cat.id] = landing;
    }
    cat.categories?.forEach(child => walk(child as Parameters<typeof walk>[0]));
  }
  tree.forEach(cat => walk(cat));
  return map;
}

/**
 * Given a product and the current locale returns an href to the product detail page.
 *
 * @param {Object} product
 * @returns {string}
 */
export const productUrlBuilder = (product: { id?: string | number }) =>
  encodeURI(`/product/${product.id}`);

/**
 * Given a search term, constructs a search url.
 *
 * @param {string} searchTerm
 * @returns {string}
 */
export const searchUrlBuilder = (searchTerm: string | number | boolean) =>
  "/search?" +
  serializeSearchParams(new URLSearchParams({ q: String(searchTerm) }));

/**
 * Maximum length for a search query sent to Constructor.io. Must fit full
 * variant product names (~90 chars observed in the catalog) while still
 * bounding arbitrary user/bot input (SFCC-11924).
 */
export const SEARCH_QUERY_MAX_LENGTH = 150;

/**
 * Caps a search query at `maxLength` without cutting mid-word: a chopped
 * trailing token (e.g. "Unsc" from "Unscented") makes Constructor.io return
 * 0 results for an otherwise-matching query (SFCC-11924).
 *
 * @param {string} query
 * @param {number} [maxLength]
 * @returns {string}
 */
export function truncateSearchQuery(
  query: string,
  maxLength: number = SEARCH_QUERY_MAX_LENGTH,
): string {
  if (query.length <= maxLength) return query;
  // Cut landing exactly between words — the sliced tail token is whole.
  if (query[maxLength] === " ") return query.slice(0, maxLength).trimEnd();
  const sliced = query.slice(0, maxLength);
  const lastSpace = sliced.lastIndexOf(" ");
  return (lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced).trimEnd();
}

/**
 * Generates the URL Template literal (Template string) used to build URLs in the App according
 * the current selected site/locale and the default App URL configuration.
 *
 * @param appConfig Application default configuration.
 * @param siteRef Current selected Site reference. The value can be the Site id or alias.
 * @param localeRef Current selected Locale reference. The value can be the Locale id or alias.
 * @returns {function(*, *, *): string} function providing: path, site and locale generates a URL.
 */
export const createUrlTemplate = (locale: Locale) => {
  const prefix = locale.id !== "default" ? `/${locale.alias || locale.id}` : "";

  return (path: string | URL, locale?: Locale) => {
    const url = new URL(path.toString(), getAppOrigin());
    url.pathname = `${prefix}${url.pathname}`;
    if (locale) {
      const prefix =
        locale.id !== "default" ? `/${locale.alias || locale.id}` : "";
      url.pathname = `${prefix}${url.pathname}`;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  };
};

/*
 * Remove query params from a give url path based on a given list of keys
 *
 * @param {string} path - The part of url to have params removed from.
 * @param {array} keys - list of params to be removed
 * @returns {string} - the url after param has been removed
 * @example
 * import {removeQueryParamsFromPath} from /path/to/util/url
 *
 * removeQueryParamsFromPath(
 *   /en-GB/cart?pid=1234&color=black&size=s&abc=12,
 *   ['pid', 'color', 'size']
 * )
 * // returns
 * // '/en-GB/cart?abc=12'
 */
export const removeQueryParamsFromPath = (path: string, keys: string[]) => {
  const [pathname, search] = path.split("?");
  const params = new URLSearchParams(search);
  keys.forEach(key => {
    if (params.has(key)) {
      params.delete(key);
    }
  });

  // Clean up any trailing `=` for params without values.
  const paramStr = params.toString().replace(/=&/g, "&").replace(/=$/, "");

  return `${pathname}${paramStr && "?"}${paramStr}`;
};
